'use client';

import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { format, isBefore, isSameDay, startOfDay, addMonths } from 'date-fns';

interface DatePickerProps {
    value?: { checkIn?: Date; checkOut?: Date };
    onChange?: (dates: { checkIn?: Date; checkOut?: Date }) => void;
    placeholder?: string;
    className?: string;
    fieldType?: 'checkIn' | 'checkOut' | 'both';
}

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const FLEXIBILITY_OPTIONS = [
    { label: 'Exact dates', value: 'exact' },
    { label: '± 1 day', value: '1' },
    { label: '± 2 days', value: '2' },
    { label: '± 3 days', value: '3' },
    { label: '± 7 days', value: '7' },
    { label: '± 14 days', value: '14' },
];

export default function DatePicker({ value, onChange, placeholder = "Add dates", className, fieldType = 'both' }: DatePickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDates, setSelectedDates] = useState<{ checkIn?: Date; checkOut?: Date }>(value || {});
    const [activeTab, setActiveTab] = useState<'dates' | 'months' | 'flexible'>('dates');
    const [flexibility, setFlexibility] = useState('exact');
    const [isSelecting, setIsSelecting] = useState<'checkIn' | 'checkOut' | null>(null);
    const [monthDuration, setMonthDuration] = useState(4);
    const [isDragging, setIsDragging] = useState(false);
    const [flexibleDuration, setFlexibleDuration] = useState<'weekend' | 'week' | 'month'>('week');
    const [selectedMonths, setSelectedMonths] = useState<Date[]>([]);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const circleRef = useRef<HTMLDivElement>(null);
    const monthScrollRef = useRef<HTMLDivElement>(null);

    // Sync internal state with props, but only if not currently selecting (open)
    useEffect(() => {
        if (value && !isOpen) {
            setSelectedDates(value);
        }
    }, [value, isOpen]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            if (direction === 'prev') {
                newDate.setMonth(prev.getMonth() - 1);
            } else {
                newDate.setMonth(prev.getMonth() + 1);
            }
            return newDate;
        });
    };

    const handleDateClick = (date: Date) => {
        const newDate = startOfDay(date);

        if (!isSelecting || isSelecting === 'checkIn') {
            setSelectedDates({ checkIn: newDate, checkOut: undefined });
            setIsSelecting('checkOut');
        } else {
            if (selectedDates.checkIn && isBefore(newDate, selectedDates.checkIn)) {
                setSelectedDates({ checkIn: newDate, checkOut: selectedDates.checkIn });
            } else {
                setSelectedDates(prev => ({ ...prev, checkOut: newDate }));
                setIsSelecting(null);
            }
        }
    };

    const isDateSelected = (date: Date | null) => {
        if (!date || !selectedDates.checkIn) return false;
        if (selectedDates.checkOut) {
            return (date >= selectedDates.checkIn && date <= selectedDates.checkOut);
        }
        return isSameDay(date, selectedDates.checkIn);
    };

    const isDateInRange = (date: Date | null) => {
        if (!date || !selectedDates.checkIn || !selectedDates.checkOut) return false;
        return date > selectedDates.checkIn && date < selectedDates.checkOut;
    };

    const isDateStart = (date: Date | null) => {
        return date && selectedDates.checkIn && isSameDay(date, selectedDates.checkIn);
    };

    const isDateEnd = (date: Date | null) => {
        return date && selectedDates.checkOut && isSameDay(date, selectedDates.checkOut);
    };

    const isDateDisabled = (date: Date | null) => {
        if (!date) return true;
        // Check if date is before today
        return isBefore(date, startOfDay(new Date()));
    };

    const isToday = (date: Date | null) => {
        if (!date) return false;
        return isSameDay(date, new Date());
    };

    const formatDisplayDate = () => {
        if (fieldType === 'checkIn') {
            if (selectedDates.checkIn) {
                return format(selectedDates.checkIn, 'MMM d');
            }
            return 'Add dates';
        } else if (fieldType === 'checkOut') {
            if (selectedDates.checkOut) {
                return format(selectedDates.checkOut, 'MMM d');
            }
            return 'Add dates';
        } else {
            if (selectedDates.checkIn && selectedDates.checkOut) {
                return `${format(selectedDates.checkIn, 'MMM d')} - ${format(selectedDates.checkOut, 'MMM d')}`;
            } else if (selectedDates.checkIn) {
                return format(selectedDates.checkIn, 'MMM d');
            }
            return placeholder;
        }
    };

    const handleApply = () => {
        onChange?.(selectedDates);
        setIsOpen(false);
    };

    const calculateMonthDates = () => {
        const startDate = new Date();
        const endDate = new Date();
        endDate.setMonth(startDate.getMonth() + monthDuration);
        return { startDate, endDate };
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging || !circleRef.current) return;

        const rect = circleRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const deltaX = e.clientX - centerX;
        const deltaY = e.clientY - centerY;

        let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
        angle = (angle + 90 + 360) % 360; // Adjust to start from top

        // Convert angle to duration (0-12 months)
        const duration = Math.round((angle / 360) * 12);
        const clampedDuration = Math.max(1, Math.min(12, duration));

        setMonthDuration(clampedDuration);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging]);

    const formatMonthDate = (date: Date) => {
        return format(date, 'MMM d, yyyy');
    };

    const generateMonthCards = () => {
        const months = [];
        const currentDate = new Date();

        // Generate 12 months starting from current month
        for (let i = 0; i < 12; i++) {
            const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
            months.push(monthDate);
        }

        return months;
    };

    const handleMonthSelect = (month: Date) => {
        setSelectedMonths(prev => {
            const isSelected = prev.some(m => isSameDay(m, month));
            if (isSelected) {
                return prev.filter(m => !isSameDay(m, month));
            } else {
                return [...prev, month];
            }
        });
    };

    const isMonthSelected = (month: Date) => {
        return selectedMonths.some(m => isSameDay(m, month));
    };

    const scrollMonths = (direction: 'left' | 'right') => {
        if (monthScrollRef.current) {
            const scrollAmount = 200;
            monthScrollRef.current.scrollBy({
                left: direction === 'right' ? scrollAmount : -scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const nextMonth = new Date(currentYear, currentMonth + 1, 1);
    const nextYear = nextMonth.getFullYear();
    const nextMonthIndex = nextMonth.getMonth();

    const currentMonthDays = getDaysInMonth(currentDate);
    const nextMonthDays = getDaysInMonth(nextMonth);

    return (
        <div className="relative" ref={dropdownRef}>
            <Button
                variant="outline"
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "w-full justify-start text-left font-normal px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all bg-white cursor-pointer",
                    className
                )}
            >
                <Calendar className="w-4 h-4 mr-2" />
                {formatDisplayDate()}
            </Button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-[600px] bg-white rounded-2xl shadow-2xl border border-gray-200 z-50">
                    {/* Tabs */}
                    <div className="flex border-b border-gray-200">
                        {[
                            { key: 'dates', label: 'Dates' },
                            { key: 'months', label: 'Months' },
                            // { key: 'flexible', label: 'Flexible' }
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key as any)}
                                className={cn(
                                    "flex-1 p-4 text-sm font-medium transition-colors",
                                    activeTab === tab.key
                                        ? "bg-gray-800 text-white"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                )}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Calendar Content */}
                    <div className="p-6">
                        {activeTab === 'dates' && (
                            <>
                                {/* Month Navigation */}
                                <div className="flex items-center justify-between mb-3">
                                    <button
                                        onClick={() => navigateMonth('prev')}
                                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <div className="flex space-x-8">
                                        <h3 className="text-lg font-semibold">
                                            {MONTHS[currentMonth]} {currentYear}
                                        </h3>
                                        <h3 className="text-lg font-semibold">
                                            {MONTHS[nextMonthIndex]} {nextYear}
                                        </h3>
                                    </div>
                                    <button
                                        onClick={() => navigateMonth('next')}
                                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Selection Status */}
                                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-center space-x-6 text-sm">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                            <span className="text-gray-700">
                                                {selectedDates.checkIn
                                                    ? format(selectedDates.checkIn, 'MMM d')
                                                    : 'Check in'
                                                }
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                            <span className="text-gray-700">
                                                {selectedDates.checkOut
                                                    ? format(selectedDates.checkOut, 'MMM d')
                                                    : 'Check out'
                                                }
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Calendar Grids */}
                                <div className="flex space-x-8">
                                    {/* Current Month */}
                                    <div className="flex-1">
                                        <div className="grid grid-cols-7 gap-1 mb-2">
                                            {DAYS.map((day, index) => (
                                                <div key={index} className="text-center text-sm font-medium text-gray-500 py-2">
                                                    {day}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="grid grid-cols-7 gap-3">
                                            {currentMonthDays.map((date, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => date && handleDateClick(date)}
                                                    disabled={!date || isDateDisabled(date)}
                                                    className={cn(
                                                        "w-8 h-8 text-sm rounded-lg transition-colors relative",
                                                        !date && "invisible",
                                                        date && !isDateDisabled(date) && "hover:bg-gray-100",
                                                        date && isDateDisabled(date) && "text-gray-300 cursor-not-allowed",
                                                        isDateSelected(date) && !isDateDisabled(date) && "bg-gray-800 text-white font-semibold hover:bg-gray-800",
                                                        isDateInRange(date) && !isDateDisabled(date) && "bg-gray-200",
                                                        isDateStart(date) && !isDateDisabled(date) && "bg-gray-800 text-white font-semibold",
                                                        isDateEnd(date) && !isDateDisabled(date) && "bg-gray-800 text-white font-semibold",
                                                        isToday(date) && !isDateSelected(date) && "ring-1 ring-gray-900 font-semibold"
                                                    )}
                                                >
                                                    {date?.getDate()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Next Month */}
                                    <div className="flex-1">
                                        <div className="grid grid-cols-7 gap-1 mb-2">
                                            {DAYS.map((day, index) => (
                                                <div key={index} className="text-center text-sm font-medium text-gray-500 py-2">
                                                    {day}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="grid grid-cols-7 gap-1">
                                            {nextMonthDays.map((date, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => date && handleDateClick(date)}
                                                    disabled={!date || isDateDisabled(date)}
                                                    className={cn(
                                                        "w-8 h-8 text-sm rounded-lg transition-colors relative",
                                                        !date && "invisible",
                                                        date && !isDateDisabled(date) && "hover:bg-gray-100",
                                                        date && isDateDisabled(date) && "text-gray-300 cursor-not-allowed",
                                                        isDateSelected(date) && !isDateDisabled(date) && "bg-gray-800 text-white font-semibold hover:bg-gray-800",
                                                        isDateInRange(date) && !isDateDisabled(date) && "bg-gray-200",
                                                        isDateStart(date) && !isDateDisabled(date) && "bg-gray-800 text-white font-semibold",
                                                        isDateEnd(date) && !isDateDisabled(date) && "bg-gray-800 text-white font-semibold",
                                                        isToday(date) && !isDateSelected(date) && "ring-1 ring-gray-900 font-semibold"
                                                    )}
                                                >
                                                    {date?.getDate()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Flexibility Options */}
                                {/* <div className="mt-6 pt-6 border-t border-gray-200">
                                    <div className="flex space-x-2">
                                        {FLEXIBILITY_OPTIONS.map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => setFlexibility(option.value)}
                                                className={cn(
                                                    "px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                                                    flexibility === option.value
                                                        ? "bg-gray-800 text-white"
                                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                )}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div> */}

                                {/* Action Buttons */}
                                <div className="flex justify-end space-x-3 mt-6">
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsOpen(false)}
                                        className="px-6"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleApply}
                                        className="px-6 bg-red-600 hover:bg-red-700 text-white"
                                    >
                                        Apply
                                    </Button>
                                </div>
                            </>
                        )}

                        {activeTab === 'months' && (
                            <div className="py-8">
                                <h3 className="text-lg font-medium text-gray-900 text-center mb-8">
                                    When's your trip?
                                </h3>

                                <div className="flex justify-center mb-8">
                                    <div
                                        ref={circleRef}
                                        className="relative w-64 h-64 cursor-pointer"
                                        onMouseDown={handleMouseDown}
                                    >
                                        {/* Outer ring with dots */}
                                        <div className="absolute inset-0 rounded-full border-4 border-gray-200">
                                            {/* Dots around the circle */}
                                            {Array.from({ length: 12 }, (_, i) => {
                                                const angle = (i * 30) - 90; // Start from top
                                                const x = 50 + 45 * Math.cos(angle * Math.PI / 180);
                                                const y = 50 + 45 * Math.sin(angle * Math.PI / 180);
                                                return (
                                                    <div
                                                        key={i}
                                                        className="absolute w-2 h-2 bg-gray-400 rounded-full transform -translate-x-1/2 -translate-y-1/2"
                                                        style={{
                                                            left: `${x}%`,
                                                            top: `${y}%`,
                                                        }}
                                                    />
                                                );
                                            })}

                                            {/* Pink arc for selected duration */}
                                            <svg className="absolute inset-0 w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
                                                <circle
                                                    cx="50%"
                                                    cy="50%"
                                                    r="45%"
                                                    fill="none"
                                                    stroke="#ec4899"
                                                    strokeWidth="8"
                                                    strokeDasharray={`${(monthDuration / 12) * 283} 283`}
                                                    strokeLinecap="round"
                                                    className="transition-all duration-200"
                                                />
                                            </svg>

                                            {/* Handle */}
                                            <div
                                                className="absolute w-6 h-6 bg-white border-2 border-pink-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 shadow-lg cursor-grab active:cursor-grabbing"
                                                style={{
                                                    left: `${50 + 45 * Math.cos(((monthDuration / 12) * 360 - 90) * Math.PI / 180)}%`,
                                                    top: `${50 + 45 * Math.sin(((monthDuration / 12) * 360 - 90) * Math.PI / 180)}%`,
                                                }}
                                            />
                                        </div>

                                        {/* Inner circle with duration */}
                                        <div className="absolute inset-8 bg-white rounded-full flex flex-col items-center justify-center shadow-inner">
                                            <span className="text-4xl font-bold text-gray-900">
                                                {monthDuration}
                                            </span>
                                            <span className="text-sm text-gray-600">
                                                {monthDuration === 1 ? 'month' : 'months'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Date range display */}
                                <div className="text-center">
                                    <div className="text-lg font-medium text-gray-900">
                                        <span className="underline">
                                            {formatMonthDate(calculateMonthDates().startDate)}
                                        </span>
                                        <span className="mx-2">to</span>
                                        <span className="underline">
                                            {formatMonthDate(calculateMonthDates().endDate)}
                                        </span>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-end space-x-3 mt-8">
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsOpen(false)}
                                        className="px-6"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            const { startDate, endDate } = calculateMonthDates();
                                            setSelectedDates({ checkIn: startDate, checkOut: endDate });
                                            onChange?.({ checkIn: startDate, checkOut: endDate });
                                            setIsOpen(false);
                                        }}
                                        className="px-6 bg-red-600 hover:bg-red-700 text-white"
                                    >
                                        Apply
                                    </Button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'flexible' && (
                            <div className="py-8">
                                {/* Duration Selection */}
                                <div className="mb-8">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        How long would you like to stay?
                                    </h3>
                                    <div className="flex space-x-3">
                                        {[
                                            { key: 'weekend', label: 'Weekend' },
                                            { key: 'week', label: 'Week' },
                                            { key: 'month', label: 'Month' }
                                        ].map((duration) => (
                                            <button
                                                key={duration.key}
                                                onClick={() => setFlexibleDuration(duration.key as any)}
                                                className={cn(
                                                    "px-6 py-3 text-sm font-medium rounded-full border-2 transition-colors",
                                                    flexibleDuration === duration.key
                                                        ? "border-gray-900 bg-gray-900 text-white"
                                                        : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                                                )}
                                            >
                                                {duration.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Month Selection */}
                                <div className="mb-8">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        When do you want to go?
                                    </h3>
                                    <div className="relative">
                                        <div
                                            ref={monthScrollRef}
                                            className="flex space-x-3 overflow-x-auto scrollbar-hide pb-2"
                                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                                        >
                                            {generateMonthCards().map((month, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleMonthSelect(month)}
                                                    className={cn(
                                                        "flex-shrink-0 w-32 h-20 bg-white border-2 rounded-lg flex flex-col items-center justify-center transition-colors",
                                                        isMonthSelected(month)
                                                            ? "border-gray-900 bg-gray-50"
                                                            : "border-gray-200 hover:border-gray-300"
                                                    )}
                                                >
                                                    <Calendar className="w-5 h-5 text-gray-600 mb-1" />
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>

                                        {/* Scroll buttons */}
                                        <button
                                            onClick={() => scrollMonths('right')}
                                            className="absolute right-0 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white border border-gray-300 rounded-full flex items-center justify-center shadow-lg hover:bg-gray-50"
                                        >
                                            <ChevronRight className="w-4 h-4 text-gray-600" />
                                        </button>
                                    </div>
                                </div>

                                {/* Selected months display */}
                                {selectedMonths.length > 0 && (
                                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Selected months:</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedMonths.map((month, index) => (
                                                <span
                                                    key={index}
                                                    className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-full"
                                                >
                                                    {format(month, 'MMMM yyyy')}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex justify-end space-x-3">
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsOpen(false)}
                                        className="px-6"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (selectedMonths.length > 0) {
                                                // Calculate dates based on selected months and duration
                                                const startDate = selectedMonths[0];
                                                let endDate = new Date(startDate);

                                                if (flexibleDuration === 'weekend') {
                                                    endDate.setDate(startDate.getDate() + 2);
                                                } else if (flexibleDuration === 'week') {
                                                    endDate.setDate(startDate.getDate() + 7);
                                                } else if (flexibleDuration === 'month') {
                                                    endDate.setMonth(startDate.getMonth() + 1);
                                                }

                                                setSelectedDates({ checkIn: startDate, checkOut: endDate });
                                                onChange?.({ checkIn: startDate, checkOut: endDate });
                                                setIsOpen(false);
                                            }
                                        }}
                                        disabled={selectedMonths.length === 0}
                                        className="px-6 bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300"
                                    >
                                        Apply
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
